import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import ButtonComponent from 'components/ButtonComponent';
import {
  Box,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import TypographyComponent from 'components/TypographyComponent';
import { styled } from '@mui/material/styles';
import { AddPhotoAlternate, PhotoCamera } from '@mui/icons-material';
import { t } from 'i18next';
import { AppContext } from 'context/AppContextProvider';

interface WebCamCaptureComponentProps {
  onCapture?: (imageSrc: string | null) => void;
  onChangeSelectedImage?: (file: File) => void;
  handleCancelEdition?: () => void;
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const WebCamCaptureComponent: React.FC<WebCamCaptureComponentProps> = ({
  onCapture,
  onChangeSelectedImage,
  handleCancelEdition,
}) => {
  const webcamRef = useRef<Webcam>(null);
  const [, setImgSrc] = useState<string | null>(null);
  const [webcamError, setWebcamError] = useState<string | null>(null);
  const [isWebcamActive, setIsWebcamActive] = useState<boolean>(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

  const { showSnackbar } = useContext(AppContext);

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot() || null;
    setImgSrc(imageSrc);
    if (onCapture) {
      onCapture(imageSrc);
    }
  }, [webcamRef, onCapture]);

  useEffect(() => {
    const getWebcamPermissions = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        setIsWebcamActive(true);
        stream.getTracks().forEach((track) => track.stop());
      } catch (error) {
        console.error('Webcam permission error:', error);
        setWebcamError(
          'Falha ao acessar recursos de câmera! Verifique as permissões de acesso a câmera.',
        );
      }
    };

    getWebcamPermissions();
  }, []);

  useEffect(() => {
    const listDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === 'videoinput',
      );
      setDevices(videoDevices);
      if (videoDevices.length > 0) {
        setSelectedDeviceId(videoDevices[0].deviceId);
      }
    };

    listDevices();
  }, []);

  const handleDeviceChange = (event: SelectChangeEvent<string>) => {
    setSelectedDeviceId(event.target.value);
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      {webcamError ? (
        <TypographyComponent variant="h6" color="error">
          {webcamError}
        </TypographyComponent>
      ) : isWebcamActive ? (
        <>
          <FormControl variant="outlined" sx={{ minWidth: 320, mb: 2 }}>
            <InputLabel id="select-webcam-label">Selecione a Webcam</InputLabel>
            <Select
              labelId="select-webcam-label"
              value={selectedDeviceId}
              onChange={handleDeviceChange}
              label="Selecione a Webcam"
              sx={{ width: '100%', height: '40px' }}
            >
              {devices.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            width={400}
            height={240}
            videoConstraints={{
              deviceId: selectedDeviceId,
              width: 1280,
              height: 720,
              facingMode: 'user',
            }}
          />
        </>
      ) : (
        <TypographyComponent variant="h6">
          Requesting webcam access...
        </TypographyComponent>
      )}
      <Grid2 container sx={{ width: '100%' }} columnSpacing={2} rowSpacing={2}>
        <Grid2 size={6}>
          <ButtonComponent
            onClick={capture}
            sx={{ mt: 2 }}
            fullWidth
            startIcon={<PhotoCamera />}
            color={'primary'}
          >
            Tirar foto
          </ButtonComponent>
        </Grid2>
        <Grid2 size={6}>
          <ButtonComponent
            onClick={handleCancelEdition}
            sx={{ mt: 2 }}
            fullWidth
            color={'error'}
          >
            Cancelar
          </ButtonComponent>
        </Grid2>
        <Grid2 size={12}>
          <ButtonComponent
            sx={{
              mt: 2,
              textAlign: 'center',
            }}
            component="label"
            fullWidth
            role={undefined}
            variant="contained"
            tabIndex={-1}
            startIcon={<AddPhotoAlternate />}
            color={'secondary'}
          >
            Adicionar foto existente
            <VisuallyHiddenInput
              type="file"
              accept="image/jpg, image/jpeg"
              onChange={(event: any) => {
                const file = event.target.files?.[0];
                const maxSizeKB = 275;

                if (file && file.size > maxSizeKB * 1024) {
                  showSnackbar(t('common.maxFileSizeError'), "error");
                  event.target.value = "";
                  return;
                }

                onChangeSelectedImage?.(file);
              }}
              multiple
            />
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default WebCamCaptureComponent;
